import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button, Box, Typography } from "@mui/material";

import QrCodeIcon from "@mui/icons-material/QrCode";
import SplitscreenIcon from "@mui/icons-material/Splitscreen";
import SnippetFolderIcon from "@mui/icons-material/SnippetFolder";
import BarChartIcon from "@mui/icons-material/BarChart";
import SettingsIcon from "@mui/icons-material/Settings";
import MiscellaneousServicesIcon from "@mui/icons-material/MiscellaneousServices";
import CodeIcon from "@mui/icons-material/Code";
import ScienceIcon from "@mui/icons-material/Science";
import FolderSpecialIcon from '@mui/icons-material/FolderSpecial';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import PaymentsIcon from '@mui/icons-material/Payments';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SchoolIcon from '@mui/icons-material/School';
import BusinessIcon from '@mui/icons-material/Business';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

import { PERMISSIONS } from "../../constants";

import { pqrsdText } from "../pqrsd/pqrsdLabel";
export function Home() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);

  // Permissions are an array on the user document, but an old or half-created
  // record may not carry it, and `user.permissions.includes(...)` blew up the
  // whole home screen. Always read it through here.
  const can = (permission) =>
    Array.isArray(user && user.permissions) && user.permissions.includes(permission);

  // El usuario-empresa NO se redirige desde aquí. Antes caía derecho en
  // /contracts/contractor, y quien no tuviera contractor.list —que era el caso
  // de toda cuenta recién registrada— rebotaba a '/', o sea otra vez a este
  // Home, otra vez a contratos: un ciclo infinito entre las dos rutas. El menú
  // ya se dibuja por permiso, así que la empresa ve sus dos baldosas (Mi
  // Entidad y Mis contratos) y escoge, como cualquier otro usuario.

  const handleRadicar = () => {
    // Single entry point for the module. The hub only renders its menu for
    // admins; everyone else is redirected straight through to the list they
    // have access to.
    navigate("/filedlist/inicio");
  };

  const handleTasks = () => {
    navigate("/mytasks");
  };

  const handleProcesses = () => {
    navigate("/processes");    
  };

  const handleProcessesTemplate = () => {    
    navigate("/processesTemplate");
  };

  const handleExpedients = (own) => {
    navigate(own ? "/expedients/own": "/expedients");
  };

  const handleContracts = () => {
    navigate("/contracts");
  };

  const handleSupervisorContracts = () => {
    navigate("/contracts/supervisor");
  };

  const handleSupportContracts = () => {
    navigate("/contracts/support");
  };

  const handleContractorContracts = () => {
    navigate("/contracts/contractor");
  };

  const handleReports = () => {
    navigate("/reports");
  };

  const handleLabs = () => {
    navigate("/labs");
  };

  const handleTrd = () => {
    navigate("/trd");
  };

  const handleSettings = () => {
    navigate("/config");
  };

  const handleAudit = () => {
    navigate("/audit");
  };

  const handleFinancing = () => {
    navigate("/financing");
  }

  const handleAdmin = () => {
    navigate("/users/home");
  };

  const handlePqrsd = () => {
    navigate("/pqrsd/inicio");
  };

  const handleExtension = () => {
    navigate("/extension/inicio");
  };

  const handleMyEntity = () => {
    navigate("/my-entity");
  };

  const MenuItem = ({ icon, label, onClick }) => {
    return (
      <Button
        variant="contained"
        color="primary"
        style={{
          textTransform: "none",
          width: "128px",
          height: "128px",
          borderRadius: "8px",
          padding: 0,
        }}
        onClick={onClick}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          {icon}
          <Typography variant="caption" style={{ marginTop: 4 }}>
            {label}
          </Typography>
        </Box>{" "}
      </Button>
    );
  };

  return (
    <Box p={2}>
      <Box display="flex" flexDirection="row" flexWrap="wrap" gap={2}>
      {/* For a company user this is the only tile on the screen: nobody in
          internal staff holds my_entity.* (not even ADMIN, which excludes it
          explicitly in constants.js). */}
      {can(PERMISSIONS.MY_ENTITY.VIEW)
        && <MenuItem
          icon={<BusinessIcon fontSize="large" />}
          label="Mi Entidad"
          onClick={handleMyEntity}
        />
      }
      {(can(PERMISSIONS.FILED.LIST)
        || can(PERMISSIONS.REQUESTSFILED.LIST))
        && <MenuItem
          icon={<QrCodeIcon fontSize="large" />}
          label="Radicados"
          onClick={handleRadicar}
        />
      }
      {can(PERMISSIONS.TASK.LIST)
        && <MenuItem
          icon={<SplitscreenIcon fontSize="large" />}
          label="Tareas"
          onClick={handleTasks}
        />
      }
      {can(PERMISSIONS.PROCESS.LIST)
        && <MenuItem
          icon={<MiscellaneousServicesIcon fontSize="large" />}
          label="Procesos"
          onClick={handleProcesses}
        />
      }
      {can(PERMISSIONS.EXPEDIENT.VIEW)
        && <MenuItem
          icon={<SnippetFolderIcon fontSize="large" />}
          label="Expedientes"
          onClick={() => handleExpedients(false)}
        />
      }
      {can(PERMISSIONS.EXPEDIENT.OWN)
        && <MenuItem
          icon={<FolderSpecialIcon fontSize="large" />}
          label="Mis Expedientes"
          onClick={() => handleExpedients(true)}
        />
      }
      {/* Una sola entrada para las dos clases: la lista muestra lo que cada
          permiso habilita. Basta con poder ver una de ellas. */}
      {(can(PERMISSIONS.CONTRACT.LIST) || can(PERMISSIONS.CONTRACT_LEGAL.LIST))
        && <MenuItem
          icon={<ContactPageIcon fontSize="large" />}
          label="Contratos"
          onClick={handleContracts}
        />
      }
      {can(PERMISSIONS.SUPERVISOR_CONTRACT.LIST)
        && <MenuItem
          icon={<ContactPageIcon fontSize="large" />}
          label="Supervisar Contratos"
          onClick={handleSupervisorContracts}
        />
      }
      {can(PERMISSIONS.SUPPORT_SUPERVISION.LIST)
        && <MenuItem
          icon={<ContactPageIcon fontSize="large" />}
          label="Apoyo a la Supervisión"
          onClick={handleSupportContracts}
        />
      }
      {can(PERMISSIONS.CONTRACTOR.LIST)
        && <MenuItem
          icon={<ContactPageIcon fontSize="large" />}
          label="Mis Contratos"
          onClick={handleContractorContracts}
        />
      }
      {can(PERMISSIONS.TEMPLATE.LIST)
        && <MenuItem
          icon={<CodeIcon fontSize="large" />}
          label="Plantillas"
          onClick={handleProcessesTemplate}
        />
      }
      {can(PERMISSIONS.REPORT.LIST)
        && <MenuItem
          icon={<BarChartIcon fontSize="large" />}
          label="Reportes"
          onClick={handleReports}
        />
      }
      {can(PERMISSIONS.LAB.LIST)
        && <MenuItem
          icon={<ScienceIcon fontSize="large" />}
          label="Laboratorio"
          onClick={handleLabs}
        />
      }
      {can(PERMISSIONS.TRD.VIEW)
        && <MenuItem
          icon={<AccountTreeIcon fontSize="large" />}
          label="Tabla de Retención"
          onClick={handleTrd}
        />
      }
      {can(PERMISSIONS.CONFIG.LIST)
        && <MenuItem
          icon={<SettingsIcon fontSize="large" />}
          label="Configuración"
          onClick={handleSettings}
        />
      }
      {can(PERMISSIONS.AUDIT.LIST)
        && <MenuItem
          icon={<FactCheckIcon fontSize="large" />}
          label="Auditoría"
          onClick={handleAudit}
        />
      }
      {can(PERMISSIONS.CONTRACT.LIST)
        && <MenuItem
          icon={<ContactPageIcon fontSize="large" />}
          label="Actas de Pago"
          onClick={handleFinancing}
        />
      }
      {/* Recorrido financiero de facturas jurídicas (acta #3). Módulo aparte de
          Actas de Pago, que es el flujo de personas naturales. */}
      {can(PERMISSIONS.PAYMENT_FLOW.LIST)
        && <MenuItem
          icon={<PaymentsIcon fontSize="large" />}
          label="Gestión de Pagos"
          onClick={() => navigate("/payments/management")}
        />
      }
      {(can(PERMISSIONS.USER_ADMIN.ADD)
        || can(PERMISSIONS.USER_ADMIN.MANAGE)
        || can(PERMISSIONS.USER_ADMIN.DEACTIVATE))
        && <MenuItem
          icon={<AdminPanelSettingsIcon fontSize="large" />}
          label="Usuarios"
          onClick={handleAdmin}
        />
      }
      {(can(PERMISSIONS.PQRSD.LIST)
        || can(PERMISSIONS.PQRSD.MINE)
        || can(PERMISSIONS.PQRSD.WINDOW_INTAKE)
        || can(PERMISSIONS.PQRSD.CONFIG)
        || can(PERMISSIONS.PQRSD.REPORTS))
        && <MenuItem
          icon={<SupportAgentIcon fontSize="large" />}
          label={pqrsdText("PQRSD")}
          onClick={handlePqrsd}
        />
      }
      {/* El hub de Extensión reúne aspirantes, convenios y entidades: basta con
          poder entrar a cualquiera de los tres. ADMIN entra por rol porque los
          permisos son una foto por usuario y agreement.* / entity.* no existen
          en las cuentas creadas antes de esos módulos. */}
      {(can(PERMISSIONS.APPLICANT.LIST)
        || can(PERMISSIONS.APPLICANT.VALIDATE_DOC)
        || can(PERMISSIONS.APPLICANT.SELECT)
        || can(PERMISSIONS.AGREEMENT.LIST)
        || can(PERMISSIONS.ENTITY.LIST)
        || (user.roles || []).includes("ADMIN"))
        && <MenuItem
          icon={<SchoolIcon fontSize="large" />}
          label="Extensión"
          onClick={handleExtension}
        />
      }
      </Box>
    </Box>
  );
}
